import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { observer } from "mobx-react-lite";
import { type FC, useState } from "react";

import { ErrorForm } from "@/components/atoms";

import { MediaUploader, UploadedFiles } from "../../MediaUploader";
import type { MediaStore } from "../../MediaUploader/mediaStore";
import { DISH_MEDIA_ACCEPTED_TYPES } from "./dishMediaConfig";

interface Props {
  mediaStore: MediaStore;
  disabled?: boolean;
}

const StepPhotos: FC<Props> = observer(({ mediaStore, disabled = false }) => {
  const [errorMedia, setErrorMedia] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = mediaStore.mediaFiles.findIndex(m => m.id === active.id);
    const newIndex = mediaStore.mediaFiles.findIndex(m => m.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      mediaStore.moveMedia(oldIndex, newIndex);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-xl font-bold">Photos & videos</h2>
      <p className="text-sm text-gray-500">
        <strong>Photo:</strong> JPG, GIF, or PNG, square 700x700 px, under 3 MB.
        <strong className="pl-1">Video:</strong> 1080p MP4, under 100 MB. Max 5
        media items.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={mediaStore.mediaFiles.map(m => m.id!)}
          strategy={horizontalListSortingStrategy}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-2">
            {mediaStore.mediaFiles.map((media, index) => (
              <UploadedFiles
                key={media.id}
                media={media}
                index={index}
                mediaStore={mediaStore}
              />
            ))}
            <MediaUploader
              acceptedTypes={DISH_MEDIA_ACCEPTED_TYPES}
              disabled={mediaStore.isTotalLimitReached || disabled}
              mediaStore={mediaStore}
              onError={setErrorMedia}
            />
          </div>
        </SortableContext>
      </DndContext>

      {errorMedia && <ErrorForm message={errorMedia} />}
    </div>
  );
});

export default StepPhotos;
